import React, { useState, useRef } from 'react';
import {
  SearchIcon,
  SmallExitIcon
} from '../../modules/Icons';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { ENV } from '../../constants/environments';
import { useRouter } from 'next/router';
import debounce from 'lodash.debounce';
import { highlightKeywords } from '../../helpers/shared';
import { getCollectSearchResultsPluginPropsFor } from '../../helpers/analytics/userInterestAnalytics';
import SendSearchAnalyticsPlugin from '../user-interest-analytics/SendSearchAnalyticsPlugin';
import CollectSearchResultsPlugin from '../user-interest-analytics/CollectSearchResultsPlugin';
import { ProductSearchResultType } from '../../types/api/ProductsIndexTypes';
import hasSelectedFilters from '../../helpers/search/hasSelectedFilters';

const DEBOUNCE_MS = 750;

export const PFLProductFilters: React.FC<any> = (props) => {
  const router = useRouter();
  const { defaults, filters, selectedFilters, setSelectedFilters } = props;

  const searchTermRef = useRef<string>('');

  let tags = [];
  filters.map((f) => {
    tags = [
      ...tags,
      ...f.option_type.option_values.map((v) => {
        v.parent_id = f.option_type.id;
        return v;
      })
    ];
  });

  let selectedTagIds = [];
  Object.values(selectedFilters).map((v: any) => {
    selectedTagIds = [...selectedTagIds, ...v];
  });

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id));

  const [searchResults, setSearchResults] = useState({});
  const [showSearchResults, setShowSearchResults] = useState(false);

  const apiUrl: string = `${ENV.api.baseURL}
  `;
  const { register, getValues } = useForm({
    defaultValues: {
      sort_by: defaults.sort_by || 'trending',
      search: defaults.search || ''
    }
  });

  const handleChange = (f) => {
    if (f.target.getAttribute('id') == 'searchField') {
      return false;
    }
    getResults(mapFieldsToParams());
  };

  const mapFieldsToParams = () => {
    return {
      ...defaults,
      sort_by: getValues('sort_by')
    };
  };

  const getResults = (params) => {
    const uri = router.pathname;
    const url = uri + '?' + new URLSearchParams(params).toString();
    window.location.href = url;
  };
  const handleSearch = debounce(async () => {
    if (getValues('search') == '') {
      setSearchResults({});
      setShowSearchResults(false);
      return;
    }

    const url = apiUrl.trim() + `/searches?cq=${getValues('search')}&from_searchbox=true&library_id=2`;

    const res = await axios.get(url);
    // use search term ref to sync search term and results tracking
    searchTermRef.current = getValues('search');
    setSearchResults(res.data);
    setShowSearchResults(true);
  }, DEBOUNCE_MS);

  const resultCount = (): any => {
    return Object.values(searchResults).reduce((acc, val: any) => acc + val.length, 0);
  };

  const resultUrl = (result: any): string => {
    switch (result.type) {
      case 'Collection':
        return `/practice-footage/${result.path}`;
      case 'Keyword':
        return `/practice-footage/${result.path}`;
    }
  };

  return (
    <form key="product-searching" onChange={handleChange} data-testid="productSearchingForm">
      <div className="pb-8">
        <div className="lg:flex w-full gap-8 items-center">
          <div className="w-full relative">
            <div className={``} data-testid="productSearchingContainer">
              <div
                className={
                  'rounded-[5px] flex items-center px-4 py-3 bg-a-dark-gray md:min-w-[400px]'
                }
              >
                <SearchIcon className="inline w-4 stroke-white" />

                {selectedTags &&
                  selectedTags.slice(0, 3).map((tag) => (
                    <span
                      key={tag.id}
                      className="hidden md:flex ml-3 mr-1 items-center justify-center text-[14px] text-white bg-a-blue px-2 py-1 whitespace-nowrap rounded-[5px] cursor-pointer hover:bg-blue-1000"
                      onClick={() => {
                        setSelectedFilters({
                          ...selectedFilters,
                          [tag.parent_id]: selectedFilters[tag.parent_id].filter(
                            (v: any) => v !== tag.id
                          )
                        });
                      }}
                    >
                      {tag.name}
                      <SmallExitIcon className="cursor-pointer inline ml-1 stroke-white fill-white h-4" />
                    </span>
                  ))}
                {selectedTags?.length > 3 && (
                  <span className="hidden md:block whitespace-nowrap text-gray-600 pl-2 pr-4 text-[12px]">
                    +{selectedTags.length - 3} more
                  </span>
                )}

                {
                  !hasSelectedFilters(selectedFilters) &&
                  <input
                    className="pl-2 w-full bg-transparent text-sm outline-0"
                    {...register('search')}
                    id="searchField"
                    type="search"
                    placeholder="Search 500+ clips"
                    onKeyUp={handleSearch}
                    onFocus={() => setShowSearchResults(true)}
                  />
                }
              </div>
              {showSearchResults && resultCount() > 0 && (
                <>
                  <div
                    className="overlay bg-black left-0 top-0 fixed bg-opacity-10 w-screen h-screen"
                    onClick={() => setShowSearchResults(false)}
                  ></div>
                  <div
                    className="absolute left-0 right-0 results-container bg-a-dark-gray rounded-b-[5px] text-white z-50 pb-5 shadow-lg drop-shadow-md mt-[2px]"
                    key={searchTermRef.current}
                  >
                    <SendSearchAnalyticsPlugin
                      mode="productsPageDropdownSearch"
                      search_type='products-pfl'
                      searchTerm={searchTermRef.current}
                    />
                    <div>
                      {Object.keys(searchResults).map((bucket) => (
                        <div key={bucket}>
                          {searchResults[bucket].length > 0 && (
                            <div className="uppercase px-5 pb-1 pt-4 text-[12px] text-gray-600">
                              {bucket == 'collections' ? 'Scenes' : bucket}
                            </div>
                          )}
                          {searchResults[bucket].map((result, k) => (
                            <a
                              key={`${searchTermRef.current}-${k}`}
                              className="px-5 text-[16px] flex gap-3 py-2 items-center hover:bg-gray-800"
                              href={resultUrl(result)}
                            >
                              {result.thumbnail && (
                                <img
                                  src={result.thumbnail}
                                  className="w-16 border border-gray-800"
                                />
                              )}
                              {bucket == 'keywords' && (
                                <SearchIcon className="inline stroke-gray-600 w-4" />
                              )}
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: highlightKeywords(getValues('search'), result.value)
                                }}
                              ></span>
                              {bucket !== ('keywords' as ProductSearchResultType) && (
                                <CollectSearchResultsPlugin
                                  {...getCollectSearchResultsPluginPropsFor(result, 'pfl', 'productsPageDropdownSearch')}
                                />
                              )}
                            </a>
                          ))}
                        </div>
                      ))}

                      <p className="text-gray-600 px-5 pt-4 text-[14px] border-t border-gray-800 mt-4">
                        Click <span className="text-gray-500 font-bold">Enter</span> for more search
                        results.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* <div className="md:min-w-[220px] text-right p-2 md:p-0 mt-4 lg:my-0">
            <label className="text-sm text-a-gray">{filterOptions.sort_by.label}</label>
            <Menu>
              <Menu.Button>
                <span className="ml-3 relative">
                  {filterOptions.sort_by?.options.find((k) => k[0] == getValues('sort_by'))[1] ||
                    'Default'}{' '}
                  <DownChevronIcon className="inline" />
                </span>
              </Menu.Button>
              <Menu.Items className="absolute bg-gray-1000 z-[999] right-6 mt-4 rounded-[5px] shadow-md overflow-hidden">
                {filterOptions.sort_by.options.map((opt) => (
                  <Menu.Item key={opt[0]}>
                    <div
                      key={opt[0]}
                      className="text-left px-4 py-2 hover:bg-gray-800 cursor-pointer text-[14px]"
                      onClick={(e) => {
                        setValue('sort_by', opt[0]);
                        handleChange(e);
                      }}
                    >
                      {opt[1]}
                    </div>
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Menu>

            <select
              {...register('sort_by')}
              className="hidden text-md ml-4 border-0 outline-0 bg-gray-900"
            >
              {filterOptions.sort_by.options.map((opt) => (
                <option key={opt[0]} value={opt[0]} className="bg-gray-900">
                  {opt[1]}
                </option>
              ))}
            </select>
          </div> */}
        </div>
      </div>
    </form>
  );
};
